import { observer } from "mobx-react-lite";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppContext } from "../../../shared/functions/Context";
import { hideModalFromId } from "../../../shared/functions/ModalShow";
import { defaultObjective, IObjective } from "../../../shared/models/Objective";
import MODAL_NAMES from "../ModalName";
import ObjectiveDepartmentForm from "./ObjectiveDepartmentForm";

const ObjectiveDepartmentModal = observer(() => {
  const { api, store } = useAppContext();
  const [objective, setObjective] = useState(defaultObjective);
  const [loading, setLoading] = useState(false);
  const { departmentId } = useParams();
  const me = store.auth.meJson;

  const companyObjectives = useMemo(() => {
    if (!me) return [];
    const objectives = store.companyObjective.all;
    return objective.perspective
      ? objectives.filter((o) => o.asJson.perspective === objective.perspective)
      : objectives;
  }, [me, objective.perspective, store.companyObjective.all]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const batch = store.scorecard.active?.id;

    if (!me || !departmentId || !batch) {
      console.warn("Missing required fields: me, departmentId, or batchId");
      return;
    }

    setLoading(true);

    const $objective: IObjective = {
      ...objective,
      uid: me.uid,
      userName: me.displayName || "",
      department: departmentId,
    };

    // Use the presence of an id on the objective to determine update vs create
    if ($objective.id) {
      console.log("Updating existing objective...");
      await update(batch, $objective);
    } else {
      console.log("Creating new objective...");
      await create(batch, $objective);
    }

    setLoading(false);
    onCancel();
  };

  const create = async (scorecardId: string, objective: IObjective) => {
    try {
      console.log("Calling API: create", objective);
      await api.departmentObjective.create(scorecardId, objective);
      console.log("Created successfully.");
    } catch (error) {
      console.error("Create failed:", error);
    }
  };

  const update = async (scorecardId: string, objective: IObjective) => {
    try {
      console.log("Calling API: update", objective);
      await api.departmentObjective.update(scorecardId, objective);
      console.log("Updated successfully.");
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const onCancel = () => {
    store.departmentObjective.clearSelected();
    setObjective({ ...defaultObjective });
    hideModalFromId(MODAL_NAMES.EXECUTION.DEPARTMENT_OBJECTIVE_MODAL);
  };

  useEffect(() => {
    if (store.departmentObjective.selected)
      setObjective({ ...store.departmentObjective.selected });
    else setObjective({ ...defaultObjective });
  }, [store.departmentObjective.selected]);

  return (
    <div className="objective-modal uk-modal-dialog uk-modal-body uk-margin-auto-vertical">
      <button className="uk-modal-close-default" type="button" data-uk-close></button>

      <h3 className="uk-modal-title">Objective</h3>

      <div className="dialog-content uk-position-relative">
        <form className="uk-form-stacked uk-grid-small" data-uk-grid onSubmit={handleSubmit}>
          <ObjectiveDepartmentForm
            companyObjectives={companyObjectives}
            objective={objective}
            setObjective={setObjective}
          />

          <div className="uk-width-1-1 uk-text-right">
            <button className="btn-text uk-margin-right" type="button" onClick={onCancel}>
              Cancel
            </button>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              Save {loading && <div data-uk-spinner="ratio: .5"></div>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});


export default ObjectiveDepartmentModal;
